import React, { useEffect, useRef, useState } from 'react';
import { 
  CurrencyDollarIcon, 
  ChevronDownIcon,
  ChevronUpIcon,
  UserIcon
} from '@heroicons/react/24/outline';
import { StartupOpportunityUtils } from '../../../../utils/startupOpportunityModels';
import { useOpportunityAnalytics } from '../../../../hooks/useOpportunityAnalytics';
import { useConnectionStatus } from '../../../../hooks/useConnectionStatus';
import ConnectionStatusButton from './ConnectionStatusButton';


/**
 * OpportunityCard component for displaying startup opportunities
 * Features company-first design with company logos and job details
 */
const OpportunityCard = ({ 
  opportunity, 
  onConnect,
  onClick,
  className = '',
  position = null, // Position in grid for analytics
  source = 'grid', // Source context for analytics
  refreshConnectionStatus = null // Trigger to refresh connection status
}) => {
  const { trackView, trackInteraction, trackConversion } = useOpportunityAnalytics();
  const { connectionStatus } = useConnectionStatus(opportunity?.id, refreshConnectionStatus);
  const hasTrackedView = useRef(false);
  const [isHovering, setIsHovering] = useState(false);
  const [showFullDescription, setShowFullDescription] = useState(false);
  const hoverStartTime = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartPosition = useRef({ x: 0, y: 0 });
  const dragThreshold = 5; // pixels

  // Track opportunity view when component mounts
  useEffect(() => {
    if (opportunity?.id && !hasTrackedView.current) {
      trackView(opportunity.id, {
        source,
        position,
        companyName: opportunity.companyName,
        fundingStage: opportunity.fundingStage,
        roleArea: opportunity.roleArea
      });
      hasTrackedView.current = true;
    }
  }, [opportunity?.id, trackView, source, position, opportunity?.companyName, opportunity?.fundingStage, opportunity?.roleArea]);



  // Handle missing opportunity data
  if (!opportunity) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 h-full">
        <div className="text-center text-gray-500 dark:text-gray-400">
          No opportunity data available
        </div>
      </div>
    );
  }

  const {
    companyName = 'Unknown Company',
    companyDescription = '',
    companyLogo = null,
    companySize = null,
    companyWebsite = null,
    companyIndustries = [],
    fundingStage = '',
    roleArea = '',
    roleTitle = '',
    roleDescription = '',
    experienceLevel = '',
    location = 'Remote',
    salaryRange = '',
    postedDate = null,
    highlights = [],
    status = 'active',
    // Company data from Firestore companies collection
    company = null
  } = opportunity;

  // Format display values
  const formattedRoleArea = StartupOpportunityUtils.formatRoleArea(roleArea);

  // Handle connect button click
  const handleConnect = (e) => {
    e.stopPropagation();
    if (onConnect && typeof onConnect === 'function') {
      // Track the connect button click
      trackInteraction(opportunity.id, 'connect_button_click', {
        source,
        position,
        companyName: opportunity.companyName,
        fundingStage: opportunity.fundingStage,
        roleArea: opportunity.roleArea
      });
      
      onConnect(opportunity);
    }
  };

  // Handle drag detection
  const handleMouseDown = (e) => {
    dragStartPosition.current = { x: e.clientX, y: e.clientY };
    setIsDragging(false);
  };

  const handleMouseMove = (e) => {
    if (dragStartPosition.current.x === 0 && dragStartPosition.current.y === 0) return;
    
    const deltaX = Math.abs(e.clientX - dragStartPosition.current.x);
    const deltaY = Math.abs(e.clientY - dragStartPosition.current.y);
    
    if (deltaX > dragThreshold || deltaY > dragThreshold) {
      setIsDragging(true);
    }
  };

  const handleMouseUp = () => {
    dragStartPosition.current = { x: 0, y: 0 };
    // Reset dragging state after a short delay to prevent click from firing
    setTimeout(() => setIsDragging(false), 50);
  };

  // Handle touch events for mobile
  const handleTouchStart = (e) => {
    const touch = e.touches[0];
    dragStartPosition.current = { x: touch.clientX, y: touch.clientY };
    setIsDragging(false);
  };

  const handleTouchMove = (e) => {
    if (dragStartPosition.current.x === 0 && dragStartPosition.current.y === 0) return;
    
    const touch = e.touches[0];
    const deltaX = Math.abs(touch.clientX - dragStartPosition.current.x);
    const deltaY = Math.abs(touch.clientY - dragStartPosition.current.y);
    
    if (deltaX > dragThreshold || deltaY > dragThreshold) {
      setIsDragging(true);
    }
  };

  const handleTouchEnd = () => {
    dragStartPosition.current = { x: 0, y: 0 };
    // Reset dragging state after a short delay to prevent click from firing
    setTimeout(() => setIsDragging(false), 50);
  };

  // Handle card click
  const handleCardClick = (e) => {
    // Don't open card if it was dragged
    if (isDragging) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    if (onClick && typeof onClick === 'function') {
      // Track the card click
      trackInteraction(opportunity.id, 'card_click', {
        source,
        position,
        companyName: opportunity.companyName,
        fundingStage: opportunity.fundingStage,
        roleArea: opportunity.roleArea
      });
      
      // Track conversion from view to details
      trackConversion(opportunity.id, 'view_to_details', {
        source,
        position,
        companyName: opportunity.companyName
      }).catch(err => console.warn('Failed to track conversion:', err));
      
      onClick(opportunity);
    }
  };

  // Create enhanced click handlers that support drag detection
  const createDragAwareClickHandlers = () => {
    if (!onClick || typeof onClick !== 'function') return {};

    // For iOS Safari, we need special handling
    if (navigator.userAgent.includes('Safari') && navigator.userAgent.includes('Mobile')) {
      return {
        onTouchStart: handleTouchStart,
        onTouchMove: handleTouchMove,
        onTouchEnd: (e) => {
          handleTouchEnd();
          // Only trigger click if not dragging
          if (!isDragging) {
            handleCardClick(e);
          }
        },
        onClick: (e) => {
          // Prevent duplicate events on iOS Safari
          if (isDragging) {
            e.preventDefault();
            e.stopPropagation();
          }
        }
      };
    }

    // For desktop and other browsers
    return {
      onMouseDown: handleMouseDown,
      onMouseMove: handleMouseMove,
      onMouseUp: handleMouseUp,
      onClick: handleCardClick
    };
  };

  const cardClickHandlers = createDragAwareClickHandlers();
  
  // Handle card hover
  const handleMouseEnter = () => {
    setIsHovering(true);
    hoverStartTime.current = Date.now();
    
    // Don't track hover events immediately to avoid tracking accidental hovers
    // We'll track on mouse leave if the hover was long enough
  };
  
  // Handle card hover end
  const handleMouseLeave = () => {
    setIsHovering(false);
    
    // Only track hover if it lasted more than 500ms
    if (hoverStartTime.current && (Date.now() - hoverStartTime.current > 500)) {
      const hoverDuration = (Date.now() - hoverStartTime.current) / 1000; // in seconds
      
      trackInteraction(opportunity.id, 'hover', {
        source,
        position,
        duration: hoverDuration,
        companyName: opportunity.companyName
      }).catch(err => console.warn('Failed to track hover:', err));
    }
    
    hoverStartTime.current = null;
  };

  // Get company data - prioritize company object from Firestore, fallback to opportunity fields
  const getCompanyData = () => {
    if (company) {
      return {
        name: company.name || companyName,
        logo: company.logo || companyLogo,
        size: company.size || companySize,
        website: company.website || companyWebsite,
        industries: company.industries || companyIndustries || [],
        description: company.description || companyDescription
      };
    }
    
    return {
      name: companyName,
      logo: companyLogo,
      size: companySize,
      website: companyWebsite,
      industries: companyIndustries || [],
      description: companyDescription
    };
  };

  // Get company logo URL or fallback
  const getCompanyLogoUrl = () => {
    const companyData = getCompanyData();
    
    // First try the Firestore logo URL
    if (companyData.logo) return companyData.logo;
    
    // Fallback to known company logos in public folder
    const logoMap = {
      'stripe': '/img/logos/stripe-logo.png',
      'airtable': '/img/logos/airtable-logo.jpeg',
      'spacex': '/img/logos/SpaceX-logo.svg',
      'aws': '/img/logos/aws-logo.png',
      'databricks': '/img/logos/databricks-logo.png',
      'openai': '/img/logos/openai-logo.jpeg',
      'deloitte': '/img/logos/deloitte-logo-.png',
      'jpmorgan': '/img/logos/jpmorgan-logo.png',
      'mckinsey': '/img/logos/mckinsey-logo.png',
      'appian': '/img/logos/appian-logo.png',
      'carlyle': '/img/logos/carlyle-logo.png'
    };
    
    const companyKey = companyData.name.toLowerCase().replace(/\s+/g, '');
    return logoMap[companyKey] || null;
  };

  // Generate company initials for fallback logo
  const getCompanyInitials = () => {
    const companyData = getCompanyData();
    return companyData.name
      .split(' ')
      .map(word => word.charAt(0).toUpperCase())
      .slice(0, 2)
      .join('');
  };

  // Format posted date
  const getPostedDateText = () => {
    if (!postedDate) return 'Recently posted';
    
    const now = new Date();
    const posted = new Date(postedDate);
    const diffTime = Math.abs(now - posted);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) return 'Posted 1 day ago';
    if (diffDays < 7) return `Posted ${diffDays} days ago`;
    if (diffDays < 30) return `Posted ${Math.ceil(diffDays / 7)} weeks ago`;
    return 'Posted over a month ago';
  };



  const logoUrl = getCompanyLogoUrl();
  const companyData = getCompanyData();

  return (
    <div 
      {...cardClickHandlers}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={`bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 
                    p-4 sm:p-6 h-full flex flex-col relative
                    hover:shadow-lg hover:shadow-gray-200/50 dark:hover:shadow-gray-900/50 
                    hover:border-gray-300 dark:hover:border-gray-600 
                    hover:-translate-y-1 hover:scale-[1.02]
                    transition-all duration-300 ease-out group
                    cursor-pointer
                    focus-within:ring-2 focus-within:ring-purple-500 focus-within:ring-offset-2 
                    dark:focus-within:ring-offset-gray-900
                    ${status !== 'active' ? 'opacity-75' : ''}
                    ${isHovering ? 'ring-1 ring-purple-300 dark:ring-purple-700' : ''}
                    ${className}`}>
      
      {/* Company Logo - centered */}
      <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700 mb-4">
        {logoUrl ? (
          <>
            <img 
              src={logoUrl} 
              alt={`${companyData.name} logo`}
              className="h-12 w-12 object-contain rounded-full"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextElementSibling.style.display = 'block';
              }}
            />
            <span 
              className="hidden text-2xl font-bold text-purple-600 dark:text-purple-400" 
              aria-hidden="true"
            >
              {getCompanyInitials()}
            </span>
          </>
        ) : (
          <span 
            className="text-2xl font-bold text-purple-600 dark:text-purple-400" 
            aria-hidden="true"
          >
            {getCompanyInitials()}
          </span>
        )}
      </div>
      
      {/* Company name - centered */}
      <h2 className="text-xl font-semibold text-gray-900 mb-2 group-hover:text-purple-800 transition-colors duration-200 dark:text-gray-100 dark:group-hover:text-purple-300 text-center">
        {companyData.name}
      </h2>
      
      {/* Company Details - Size, Location, Posted Date */}
      <div className="flex flex-wrap justify-center gap-1.5 mb-3">
        {/* Company size */}
        {companyData.size && (
          <span className="inline-flex items-center rounded-full bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-600/20 dark:bg-blue-900/30 dark:text-blue-300 dark:ring-blue-500/30">
            <UserIcon className="h-3 w-3 mr-1" />
            {companyData.size}
          </span>
        )}
        
        {/* Location */}
        <span className="inline-flex items-center rounded-full bg-gray-50 px-2.5 py-1 text-xs font-medium text-gray-700 ring-1 ring-inset ring-gray-600/20 dark:bg-gray-800 dark:text-gray-300 dark:ring-gray-500/30">
          {location}
        </span>
        
        {/* Posted date */}
        <span className="inline-flex items-center rounded-full bg-gray-50 px-2.5 py-1 text-xs font-medium text-gray-700 ring-1 ring-inset ring-gray-600/20 dark:bg-gray-800 dark:text-gray-300 dark:ring-gray-500/30">
          {getPostedDateText()}
        </span>
      </div>

      {/* Company Description - Always reserve space for 2 lines */}
      <div className="mb-3" style={{ minHeight: '2.8em' }}>
        {companyData.description ? (
          <p className="text-center text-sm leading-relaxed text-gray-500 dark:text-gray-300 
                        overflow-hidden"
             style={{
               display: '-webkit-box',
               WebkitLineClamp: 2,
               WebkitBoxOrient: 'vertical',
               lineHeight: '1.4',
               maxHeight: '2.8em' // 2 lines * 1.4 line-height
             }}>
            {companyData.description}
          </p>
        ) : (
          // Empty placeholder to maintain consistent spacing
          <div style={{ height: '2.8em' }} />
        )}
      </div>



      {/* Opportunity Details Section */}
      <div className="border-t border-gray-100 dark:border-gray-700 pt-4 mb-4 flex-grow">
                {/* Role Title with Expand Button */}
        <div className="flex items-start justify-between mb-2">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white line-clamp-1 flex-1 pr-2">
            {roleTitle || formattedRoleArea || 'Open Role'}
          </h3>
          {/* Expand/Collapse Button - Only show if role description is long */}
          {roleDescription && roleDescription.length > 100 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowFullDescription(!showFullDescription);
              }}
              className="text-gray-400 hover:text-purple-600 dark:hover:text-purple-400 
                         transition-colors flex-shrink-0 p-1 -m-1"
              aria-label={showFullDescription ? 'Show less' : 'Show more'}
            >
              {showFullDescription ? (
                <ChevronUpIcon className="w-4 h-4" />
              ) : (
                <ChevronDownIcon className="w-4 h-4" />
              )}
            </button>
          )}
        </div>

        {/* Salary Range if available */}
        {salaryRange && (
          <div className="flex items-center gap-1 mb-3 text-gray-600 dark:text-gray-300">
            <CurrencyDollarIcon className="w-4 h-4 flex-shrink-0" />
            <span className="text-sm font-medium">{salaryRange}</span>
          </div>
        )}

        {/* Role Description - Always reserve minimum space */}
        <div className="mb-3" style={{ minHeight: showFullDescription ? 'auto' : '2.8em' }}>
          {roleDescription ? (
            <p className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed overflow-hidden"
               style={{
                 display: '-webkit-box',
                 WebkitLineClamp: showFullDescription ? 'none' : 2,
                 WebkitBoxOrient: 'vertical',
                 lineHeight: '1.4',
                 maxHeight: showFullDescription ? 'none' : '2.8em' // 2 lines * 1.4 line-height
               }}>
              {roleDescription}
            </p>
          ) : (
            // Empty placeholder to maintain consistent spacing when no role description
            <div style={{ height: '2.8em' }} />
          )}
        </div>

        {/* Requirements */}
        {highlights.length > 0 && (
          <div className="mb-3">
            <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Requirements:
            </div>
            <ul className="space-y-1.5">
              {highlights.slice(0, showFullDescription ? highlights.length : 2).map((highlight, index) => (
                <li key={index} className="text-sm text-gray-600 dark:text-gray-400 flex items-start gap-2">
                  <span className="text-gray-400 mt-1 flex-shrink-0 text-xs">•</span>
                  <span className="leading-relaxed">{highlight}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Connect Button */}
      <div className="mt-auto pt-3">
        <ConnectionStatusButton
          connectionStatus={connectionStatus}
          onConnect={() => handleConnect({ stopPropagation: () => {} })}
          disabled={status !== 'active'}
        />
      </div>

      {/* Status Indicator for Non-Active Opportunities */}
      {status !== 'active' && (
        <div className="absolute top-3 right-3">
          <div className={`px-2 py-1 rounded-full text-xs font-medium ${
            status === 'paused' 
              ? 'bg-yellow-100 text-yellow-800 border border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-300 dark:border-yellow-700'
              : 'bg-gray-100 text-gray-800 border border-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600'
          }`}>
            {status === 'paused' ? 'Paused' : 'Filled'}
          </div>
        </div>
      )}
    </div>
  );
};

export default OpportunityCard;